import React, { useState, useEffect } from 'react';

const API_BASE_URL = 'http://localhost:3000';

const BuscarAgricultoresPage = ({ onNavigate }) => {
  const [agricultores, setAgricultores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [raioKm, setRaioKm] = useState(50);
  const [debounceTimeout, setDebounceTimeout] = useState(null);

  const restauranteId = localStorage.getItem('restauranteId');

  useEffect(() => {
    if (restauranteId) {
      // Debounce: espera 500ms após o usuário parar de mexer no slider
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      const timeout = setTimeout(() => {
        buscarAgricultores();
      }, 500);

      setDebounceTimeout(timeout);

      return () => clearTimeout(timeout);
    }
  }, [raioKm]); // Busca automaticamente quando raioKm muda

  const buscarAgricultores = async () => {
    setLoading(true);
    setError('');

    try {
      console.log('Buscando agricultores próximos...', { restauranteId, raioKm });
      
      const response = await fetch(
        `${API_BASE_URL}/agricultores?restauranteId=${restauranteId}`
      );

      console.log('Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Agricultores encontrados:', data.length, data);
        
        // Filtrar por raio de busca
        const agricultoresFiltrados = filtrarPorRaio(data, raioKm);
        console.log('Agricultores após filtro de raio:', agricultoresFiltrados.length);
        
        setAgricultores(agricultoresFiltrados);
      } else {
        const errorData = await response.json();
        console.error('Erro na resposta:', errorData);
        setError(errorData.error || 'Erro ao buscar agricultores');
      }
    } catch (error) {
      console.error('Erro na busca:', error);
      setError('Erro de conexão com o servidor');
    } finally {
      setLoading(false);
    }
  };

  // Função para filtrar agricultores por raio
  const filtrarPorRaio = (agricultores, raioMaximo) => {
    return agricultores.filter(agricultor => {
      // Extrair o número da distância (ex: "28.1 km" -> 28.1)
      const distanciaMatch = agricultor.distancia.match(/^([\d.]+)/);
      if (!distanciaMatch) return false;
      
      const distanciaKm = parseFloat(distanciaMatch[1]);
      return distanciaKm <= raioMaximo;
    }).sort((a, b) => {
      // Ordenar por distância (mais próximo primeiro)
      const distA = parseFloat(a.distancia.match(/^([\d.]+)/)[1]);
      const distB = parseFloat(b.distancia.match(/^([\d.]+)/)[1]);
      return distA - distB;
    });
  };

  const handleRaioChange = (e) => {
    setRaioKm(e.target.value);
    // A busca será disparada automaticamente pelo useEffect
  };

  const insumosSemCadastro = agricultores.length === 0 && !loading;

  return (
    <div className="search-page-container">
      {/* Header com gradiente */}
      <div className="search-page-header">
        <div className="search-header-content">
          <div className="search-welcome">
            <h1 className="search-title">Buscar Agricultores</h1>
            <p className="search-subtitle">
              Encontre produtores locais com os ingredientes que você precisa
            </p>
          </div>
          <button 
            className="btn-back"
            onClick={() => onNavigate('Perfil')}
          >
            <span className="btn-icon">←</span>
            Voltar ao Perfil
          </button>
        </div>
      </div>

      {/* Conteúdo principal */}
      <div className="search-page-content">
        {/* Card de Filtros */}
        <div className="search-filter-section">
          <div className="filter-header">
            <div className="filter-title-group">
              <h3 className="filter-title">
                <span className="filter-icon">🎯</span>
                Raio de Busca
              </h3>
              {loading && (
                <span className="loading-badge">
                  <span className="loading-dot"></span>
                  Buscando...
                </span>
              )}
            </div>
            <div className="filter-value-display">
              <span className="value-number">{raioKm}</span>
              <span className="value-unit">km</span>
            </div>
          </div>

          <div className="slider-container">
            <div className="slider-labels">
              <span className="slider-label-start">5 km</span>
              <span className="slider-label-end">200 km</span>
            </div>
            <input
              type="range"
              min="5"
              max="200"
              step="5"
              value={raioKm}
              onChange={handleRaioChange}
              className="range-slider-modern"
            />
            <div className="slider-description">
              Ajuste para encontrar agricultores mais próximos ou expandir sua busca
            </div>
          </div>
        </div>

        {/* Mensagens de erro */}
        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span className="alert-text">{error}</span>
          </div>
        )}

        {/* Resultado da busca */}
        {loading && (
          <div className="search-loading-state">
            <div className="loading-spinner-large"></div>
            <h3 className="loading-title">Buscando agricultores próximos...</h3>
            <p className="loading-description">
              Procurando produtores em um raio de {raioKm} km com produtos relacionados aos seus insumos
            </p>
          </div>
        )}

        {!loading && insumosSemCadastro && (
          <div className="search-empty-state">
            <span className="empty-icon-search">🔍</span>
            <h3 className="empty-title-search">Nenhum agricultor encontrado</h3>
            <p className="empty-description-search">
              Não encontramos produtores no raio de <strong>{raioKm} km</strong> próximos a você.
            </p>
            <div className="empty-suggestions">
              <h4>💡 Sugestões:</h4>
              <ul>
                <li>Aumente o raio de busca usando o controle acima</li>
                <li>Os agricultores cadastrados podem estar mais distantes</li>
                <li>Tente expandir o raio para até 200 km</li>
              </ul>
            </div>
          </div>
        )}

        {!loading && agricultores.length > 0 && (
          <>
            <div className="search-results-header">
              <h3 className="results-title">
                <span className="results-icon">✓</span>
                {agricultores.length} {agricultores.length === 1 ? 'Agricultor Encontrado' : 'Agricultores Encontrados'}
              </h3>
              <p className="results-subtitle">
                Ordenados por proximidade • Raio de {raioKm} km
              </p>
            </div>

            <div className="agricultores-grid-modern">
              {agricultores.map((agricultor) => (
                <div key={agricultor.id} className="agricultor-card-modern">
                  <div className="card-badge-distance">
                    <span className="badge-icon">📍</span>
                    {agricultor.distancia}
                  </div>

                  <div className="card-header-modern">
                    <div className="farmer-avatar">🌾</div>
                    <div className="farmer-info">
                      <h3 className="farmer-name">{agricultor.nome}</h3>
                      <p className="farmer-location">
                        {agricultor.cidade}, {agricultor.estado}
                      </p>
                    </div>
                  </div>

                  <div className="card-details">
                    <div className="detail-item">
                      <span className="detail-icon">📍</span>
                      <div className="detail-content">
                        <span className="detail-label">Endereço</span>
                        <span className="detail-value">
                          {agricultor.logradouro}, {agricultor.numero}
                        </span>
                      </div>
                    </div>

                    <div className="detail-item">
                      <span className="detail-icon">🚗</span>
                      <div className="detail-content">
                        <span className="detail-label">Tempo de viagem</span>
                        <span className="detail-value">{agricultor.duracao}</span>
                      </div>
                    </div>

                    {agricultor.telefone && (
                      <div className="detail-item">
                        <span className="detail-icon">📞</span>
                        <div className="detail-content">
                          <span className="detail-label">Telefone</span>
                          <span className="detail-value">{agricultor.telefone}</span>
                        </div>
                      </div>
                    )}

                    {agricultor.email && (
                      <div className="detail-item">
                        <span className="detail-icon">✉️</span>
                        <div className="detail-content">
                          <span className="detail-label">E-mail</span>
                          <span className="detail-value email-value">{agricultor.email}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <button className="btn-contact-modern">
                    <span className="btn-icon">📞</span>
                    Entrar em Contato
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BuscarAgricultoresPage;
